import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Plus, Upload, Download, Trash2, Check, X, Search, Bell } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";


export default function GuestListManager() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    max_companions: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const siteRes = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (siteRes.ok) {
          const siteData = await siteRes.json();
          if (siteData) {
            setSite(siteData);
            const guestsRes = await fetch(`${API}/sites/${siteData.site_id}/guest-list`, { credentials: 'include' });
            if (guestsRes.ok) {
              const guestsData = await guestsRes.json();
              setGuests(guestsData);
            }
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", phone: "", max_companions: 0 });
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guest-list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          max_companions: parseInt(formData.max_companions) || 0
        })
      });

      if (res.ok) {
        const guest = await res.json();
        setGuests(prev => [...prev, guest]);
        toast.success("Convidado adicionado!");
        setDialogOpen(false);
        resetForm();
      }
    } catch (error) {
      toast.error("Erro ao adicionar convidado");
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guest-list/import`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        
        // Refresh guest list
        const guestsRes = await fetch(`${API}/sites/${site.site_id}/guest-list`, { credentials: 'include' });
        if (guestsRes.ok) {
          const guestsData = await guestsRes.json();
          setGuests(guestsData);
        }
      }
    } catch (error) {
      toast.error("Erro ao importar lista");
    }
    
    e.target.value = '';
  };

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guest-list/export`, {
        credentials: 'include'
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `convidados_${site.slug}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Lista exportada!");
      }
    } catch (error) {
      toast.error("Erro ao exportar lista");
    }
  };

  const handleDeleteGuest = async (guestId) => {
    if (!window.confirm("Remover este convidado da lista?")) return;

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guest-list/${guestId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setGuests(prev => prev.filter(g => g.guest_id !== guestId));
        toast.success("Convidado removido");
      }
    } catch (error) {
      toast.error("Erro ao remover convidado");
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Tem certeza que deseja limpar toda a lista de convidados? Esta ação não pode ser desfeita.")) return;

    try {
      const res = await fetch(`${API}/sites/${site.site_id}/guest-list`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setGuests([]);
        toast.success("Lista limpa!");
      }
    } catch (error) {
      toast.error("Erro ao limpar lista");
    }
  };

  const filteredGuests = guests.filter(g => 
    g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const confirmedCount = guests.filter(g => g.confirmed).length;
  const totalWithCompanions = guests.filter(g => g.confirmed).reduce((sum, g) => sum + 1 + (g.confirmed_companions || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-[#6E0F1A]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-[#B3121B]" />
              <span className="font-serif text-xl text-[#6E0F1A]">Lista de Convidados</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              className="hidden"
              id="csv-import"
            />
            <Button
              variant="outline"
              onClick={() => navigate('/notifications')}
              className="border-[#567D50] text-[#567D50]"
              data-testid="notifications-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Notificações
            </Button>
            <Button
              variant="outline"
              onClick={() => document.getElementById('csv-import').click()}
              className="border-[#6E0F1A] text-[#6E0F1A]"
            >
              <Upload className="w-4 h-4 mr-2" />
              Importar CSV
            </Button>
            {guests.length > 0 && (
              <Button
                variant="outline"
                onClick={handleExportCSV}
                className="border-[#B3121B] text-[#B3121B]"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl text-[#6E0F1A]">
                    Adicionar Convidado
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddGuest} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name" className="text-[#6E0F1A]">Nome *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-wedding mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-[#6E0F1A]">E-mail</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="input-wedding mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#6E0F1A]">Telefone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-wedding mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_companions" className="text-[#6E0F1A]">Acompanhantes Permitidos</Label>
                    <Input
                      id="max_companions"
                      name="max_companions"
                      type="number"
                      min="0"
                      value={formData.max_companions}
                      onChange={handleChange}
                      className="input-wedding mt-2"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full py-6"
                  >
                    Adicionar Convidado
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Total na Lista</p>
              <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">{guests.length}</p>
            </CardContent>
          </Card>
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Confirmados</p>
              <p className="text-3xl font-semibold text-[#567D50] mt-1">{confirmedCount}</p>
            </CardContent>
          </Card>
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Pendentes</p>
              <p className="text-3xl font-semibold text-yellow-600 mt-1">{guests.length - confirmedCount}</p>
            </CardContent>
          </Card>
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Total c/ Acomp.</p>
              <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">{totalWithCompanions}</p>
            </CardContent>
          </Card>
        </div>

        {/* Info about CSV format */}
        <Card className="card-dashboard mb-6 bg-[#F3C9CF]/10 border-[#F3C9CF]">
          <CardContent className="py-4">
            <p className="text-sm text-[#6E0F1A]">
              <strong>Formato do CSV para importação:</strong> O arquivo deve ter colunas: <code>nome</code>, <code>email</code>, <code>telefone</code>, <code>acompanhantes</code>
            </p>
          </CardContent>
        </Card>

        {/* Search and Actions */}
        {guests.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6E0F1A]/40" />
              <Input
                placeholder="Buscar convidado..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-wedding pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={handleClearAll}
              className="border-red-500 text-red-500 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Limpar Lista
            </Button>
          </div>
        )}

        {/* Guest List */}
        {guests.length === 0 ? (
          <Card className="card-dashboard text-center py-16">
            <CardContent>
              <div className="w-20 h-20 bg-[#F3C9CF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-[#B3121B]" />
              </div>
              <h2 className="font-serif text-2xl text-[#6E0F1A] mb-3">
                Nenhum convidado na lista
              </h2>
              <p className="text-[#6E0F1A]/70 mb-6">
                Adicione convidados manualmente ou importe uma lista CSV
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-dashboard overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAFA]">
                      <TableHead className="text-[#6E0F1A]">Nome</TableHead>
                      <TableHead className="text-[#6E0F1A]">Contato</TableHead>
                      <TableHead className="text-[#6E0F1A]">Acompanhantes</TableHead>
                      <TableHead className="text-[#6E0F1A]">Status</TableHead>
                      <TableHead className="text-[#6E0F1A]">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredGuests.map((guest) => (
                      <TableRow key={guest.guest_id}>
                        <TableCell>
                          <p className="font-medium text-[#6E0F1A]">{guest.name}</p>
                          {guest.message && (
                            <p className="text-xs text-[#6E0F1A]/50 mt-1">"{guest.message}"</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-[#6E0F1A]/70">
                            {guest.email && <p>{guest.email}</p>}
                            {guest.phone && <p>{guest.phone}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          {guest.confirmed ? (
                            <span className="text-[#6E0F1A]">
                              {guest.confirmed_companions || 0} / {guest.max_companions || 0}
                            </span>
                          ) : (
                            <span className="text-[#6E0F1A]/50">
                              Máx: {guest.max_companions || 0}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {guest.confirmed ? (
                            <Badge className="bg-[#567D50]">
                              <Check className="w-3 h-3 mr-1" />
                              Confirmado
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                              Pendente
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteGuest(guest.guest_id)}
                            className="text-[#6E0F1A]/60 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
